import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
  createColumnHelper,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { BindingTable } from '../types';
import {
  DocumentArrowDownIcon,
  DocumentTextIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import FloatingHint from './FloatingHint';
import workCategories from '../data/categories.js';

// Компонент для редактируемой ячейки
const EditableCell: React.FC<{
  initialValue: any;
  onValueChange: (value: string) => void;
  isTextarea?: boolean;
}> = ({ initialValue, onValueChange, isTextarea = false }) => {
  const [value, setValue] = useState(String(initialValue || ''));
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      // Динамически изменяем высоту textarea
      textareaRef.current.style.height = 'auto'; // Сначала сбрасываем высоту
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`; // Устанавливаем по содержимому
    }
  }, [value]);

  const handleBlur = () => {
    onValueChange(value);
  };

  if (isTextarea) {
    return (
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={handleBlur}
        className="w-full px-2 py-1 border-0 bg-transparent focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white resize-none"
        style={{ overflow: 'hidden' }} // Прячем скроллбар
        rows={1}
      />
    );
  }

  return (
    <input
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={handleBlur}
      className="w-full px-2 py-1 border-0 bg-transparent focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white"
    />
  );
};

interface TableEditorProps {
  bindingTable?: BindingTable | null;
  vorTable?: BindingTable | null;
  viewMode: 'binding' | 'vor';
  onSaveTable?: (data: any[]) => void;
  onGenerateVor?: () => void;
  onDownloadVor: () => void;
  isCorrected?: boolean;
  vorPath: string | null;
  isLoading: boolean;
}

const TableEditor: React.FC<TableEditorProps> = ({
  bindingTable,
  vorTable,
  viewMode,
  onSaveTable,
  onGenerateVor,
  onDownloadVor,
  isCorrected,
  vorPath,
  isLoading,
}) => {
  const [data, setData] = useState<any[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const nextId = useRef(0); // Для генерации уникальных ID
  const [hint, setHint] = useState<{
    rowIndex: number;
    columnId: string;
    rect: DOMRect;
    value: string;
  } | null>(null);

  // Ref для контейнера таблицы, который будет скроллироваться
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const columnHelper = createColumnHelper<any>();

  const estimateSize = useCallback(() => 45, []);

  useEffect(() => {
    const currentTable = viewMode === 'binding' ? bindingTable : vorTable;
    if (currentTable) {
      // При смене таблицы сбрасываем счетчик и назначаем новые уникальные ID
      let currentId = 0;
      const dataWithIds = currentTable.data.map((row) => ({
        ...row,
        id: currentId++,
      }));
      setData(dataWithIds);
      nextId.current = currentId; // Сохраняем следующее значение ID
    } else {
      setData([]);
      nextId.current = 0;
    }
  }, [bindingTable, vorTable, viewMode]);

  // Выносим функцию обновления данных за пределы useMemo, чтобы избежать цикла
  const updateData = (rowIndex: number, columnId: string, value: any) => {
    setData((old) =>
      old.map((row, index) => {
        if (index === rowIndex) {
          return {
            ...old[rowIndex],
            [columnId]: value,
          };
        }
        return row;
      })
    );
    setIsEditing(true);
  };

  const handleAddRowAfter = useCallback((rowIndex: number) => {
    const currentTable = viewMode === 'binding' ? bindingTable : vorTable;
    if (!currentTable) return;
    const newRow: any = { id: nextId.current++ };
    currentTable.columns.forEach((col) => {
      newRow[col] = '';
    });
    setData((oldData) => {
      const newData = [...oldData];
      newData.splice(rowIndex + 1, 0, newRow);
      return newData;
    });
    setIsEditing(true);
  }, [viewMode, bindingTable, vorTable]);

  const handleDeleteRow = useCallback((rowIndex: number) => {
    if (window.confirm('Вы уверены, что хотите удалить эту строку?')) {
      setData((oldData) => {
        if (oldData.length <= 1) {
          toast.error('Нельзя удалить последнюю строку');
          return oldData;
        }
        const newData = oldData.filter((_, index) => index !== rowIndex);
        setIsEditing(true);
        return newData;
      });
    }
  }, []);

  const columns = useMemo<ColumnDef<any>[]>(() => {
    const currentTable = viewMode === 'binding' ? bindingTable : vorTable;
    if (!currentTable) return [];

    const cols: ColumnDef<any>[] = [];

    // Добавляем колонку действий только для таблицы привязки (редактируемая)
    if (viewMode === 'binding') {
      cols.push(
        columnHelper.display({
          id: 'actions',
          header: '',
          cell: ({ row }) => (
            <div className="flex items-center justify-center space-x-1">
              <button
                onClick={() => handleDeleteRow(row.index)}
                className="p-1 text-red-300 hover:text-red-600 hover:bg-red-50 rounded transition-all duration-200 opacity-60 group-hover:opacity-100"
                title="Удалить строку"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleAddRowAfter(row.index)}
                className="p-1 text-green-300 hover:text-green-600 hover:bg-green-50 rounded transition-all duration-200 opacity-60 group-hover:opacity-100"
                title="Добавить строку после этой"
              >
                <PlusIcon className="h-4 w-4" />
              </button>
            </div>
          ),
          size: 80,
        })
      );
    }

    // Добавляем колонки данных
    currentTable.columns.forEach((col) => {
      const upperColName = col.toUpperCase();
      const isMultilineColumn =
        upperColName === 'ВИД РАБОТ' ||
        upperColName === 'НАИМЕНОВАНИЕ' ||
        upperColName === 'ЗАГОЛОВОК';

      let columnSize = 150;
      if (upperColName === 'ВИД РАБОТ' || upperColName === 'ЗАГОЛОВОК') {
        columnSize = 400;
      } else if (upperColName === 'НАИМЕНОВАНИЕ') {
        columnSize = 300;
      }

      if (viewMode==='binding') {
        if (upperColName === 'ВИД РАБОТ') {
          cols.push(
            columnHelper.accessor(row=>row[col],{
              id: col,
              header: col,
              cell: ({ getValue, row, column }) => {
                const val=String(getValue()||'');
                return (
                  <div
                    tabIndex={0}
                    onFocus={(e)=>{
                      const rect=e.currentTarget.getBoundingClientRect();
                      setHint({rowIndex: row.index, columnId: column.id, rect, value: val});
                    }}
                    className="px-2 py-1 cursor-text outline-none w-full"
                  >
                    {val}
                  </div>
                );
              },
              size:400,
            })
          );
          return; // skip pushing default below
        }
      }

      cols.push(
        columnHelper.accessor(row => row[col], {
          id: col,
          header: col,
          cell: ({ getValue, row, column }) => {
            if (viewMode === 'vor') {
              // Для ВОР - только отображение (не редактируемо)
              return (
                <div
                  className={`px-2 py-1 text-sm ${
                    isMultilineColumn ? 'whitespace-normal' : ''
                  }`}
                >
                  {String(getValue() || '')}
                </div>
              );
            }

            // Для таблицы привязки - редактируемо
            // Используем автодополнение для колонки "ВИД РАБОТ"
            if (upperColName === 'ВИД РАБОТ') {
              const val = String(getValue() || '');
              return (
                <div
                  tabIndex={0}
                  onFocus={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setHint({ rowIndex: row.index, columnId: column.id, rect, value: val });
                  }}
                  className="px-2 py-1 cursor-text outline-none w-full"
                >
                  {val}
                </div>
              );
            }
            
            // Для остальных колонок - обычное редактируемое поле
            return (
              <EditableCell
                initialValue={getValue()}
                onValueChange={(value) => updateData(row.index, column.id, value)}
                isTextarea={isMultilineColumn}
              />
            );
          },
          size: columnSize,
        })
      );
    });

    return cols;
  }, [bindingTable, vorTable, viewMode, handleAddRowAfter, handleDeleteRow, columnHelper]);

  const table = useReactTable({
    data,
    columns,
    getRowId: (row) => row.id, // Используем стабильный ID для строк
    getCoreRowModel: getCoreRowModel(),
    // Не забываем указать, что мы хотим, чтобы React Table знал о данных
    state: {
      // data, // Это не нужно, так как data передается напрямую
    },
  });

  const { rows } = table.getRowModel();

  // Настраиваем виртуализатор
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize,
    overscan: 10,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const paddingTop = virtualRows.length > 0 ? virtualRows[0]?.start || 0 : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? totalSize - (virtualRows[virtualRows.length - 1]?.end || 0)
      : 0;

  const handleSave = () => {
    if (!bindingTable || viewMode !== 'binding') return;

    const cleanedData = data.map(({ id, ...row }) => row); // Удаляем id перед сохранением
    if(onSaveTable) {
      onSaveTable(cleanedData);
    }
    setIsEditing(false);
  };

  const currentTable = viewMode === 'binding' ? bindingTable : vorTable;
  if (!currentTable) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Нет данных для отображения</h3>
          <p className="mt-1 text-sm text-gray-500">
             Выберите проект и таблицу для начала работы.
           </p>
        </div>
      </div>
    );
  }

  // Применяем стили для переноса слов в ячейках просмотра
  if (viewMode === 'vor') {
    const workTypeColumn = columns.find(c => c.id?.toUpperCase() === 'ВИД РАБОТ');
    if (workTypeColumn) {
      // @ts-ignore
      workTypeColumn.cell = ({ getValue }) => (
        <div className="px-2 py-1 text-sm whitespace-normal">
          {String(getValue() || '')}
        </div>
      );
    }
    const nameColumn = columns.find(c => c.id?.toUpperCase() === 'НАИМЕНОВАНИЕ');
    if (nameColumn) {
      // @ts-ignore
      nameColumn.cell = ({ getValue }) => (
        <div className="px-2 py-1 text-sm whitespace-normal">
          {String(getValue() || '')}
        </div>
      );
    }
    const titleColumn = columns.find(c => c.id?.toUpperCase() === 'ЗАГОЛОВОК');
    if (titleColumn) {
      // @ts-ignore
      titleColumn.cell = ({ getValue }) => (
        <div className="px-2 py-1 text-sm whitespace-normal">
          {String(getValue() || '')}
        </div>
      );
    }
  }

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Заголовок и табы */}
      <div className="border-b border-gray-200 flex-shrink-0">
        <div className="px-4 py-3">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">
              {viewMode === 'binding' ? 'Таблица привязки' : 'ВОР'}
            </h3>
            {viewMode === 'vor' && (
              <button
                onClick={onDownloadVor}
                className="inline-flex items-center px-4 py-2 border border-green-300 rounded-md shadow-sm text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100"
              >
                <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                Скачать ВОР
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Панель инструментов - только для таблицы привязки */}
      {viewMode === 'binding' && (
        <div className="bg-gray-50 border-b border-gray-200 p-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <button
                onClick={onGenerateVor}
                disabled={!isCorrected || isLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                 <DocumentTextIcon className="h-4 w-4 mr-2" />
                Сформировать ВОР
              </button>
            </div>
            <div className="flex items-center space-x-2">
              {isEditing && (
                <span className="text-sm text-orange-600">Несохраненные изменения</span>
              )}
              <button
                onClick={handleSave}
                disabled={!isEditing || isLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Таблица данных */}
      <div ref={tableContainerRef} className="flex-1 overflow-auto min-h-0">
        <table className="min-w-full divide-y divide-gray-200 border-separate" style={{ borderSpacing: 0 }}>
          <thead className="bg-gray-100 sticky top-0 z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider border-b border-gray-300"
                    style={{
                      width: header.getSize(),
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200 relative">
            {paddingTop > 0 && (
               <tr style={{ height: `${paddingTop}px` }}>
                 <td colSpan={columns.length} />
               </tr>
             )}
             {virtualRows.map((virtualRow) => {
               const row = rows[virtualRow.index];
               return (
                 <tr
                   key={row.id}
                   data-index={virtualRow.index}
                   ref={rowVirtualizer.measureElement}
                   className="group hover:bg-gray-50"
                 >
                   {row.getVisibleCells().map((cell) => (
                     <td
                       key={cell.id}
                       className="px-0 py-0 align-top border-b border-gray-200"
                       style={{ width: cell.column.getSize() }}
                     >
                       {flexRender(
                         cell.column.columnDef.cell,
                         cell.getContext()
                       )}
                     </td>
                   ))}
                 </tr>
               );
             })}
             {paddingBottom > 0 && (
               <tr style={{ height: `${paddingBottom}px` }}>
                 <td colSpan={columns.length} />
               </tr>
             )}
          </tbody>
        </table>
      </div>
      {hint && (
         <FloatingHint
            anchorRect={hint.rect}
            value={hint.value}
            categories={workCategories}
            onSelect={(cat)=>{
               updateData(hint.rowIndex, hint.columnId, cat);
               setHint(null);
               setIsEditing(true);
            }}
            onClose={()=> setHint(null)}
         />
      )}
    </div>
  );
};

export default TableEditor;